// What the seven diagnoses mean, in one place.
//
// The colours were previously a local map inside ServerCard, which was fine
// while the card was the only thing that showed a status. The Players tab and
// the legend show it too, and a legend whose amber does not match the card's
// amber is worse than no legend at all.

import { Diagnosis } from './api'

export const STATUS_STYLE: Record<Diagnosis, string> = {
    healthy: 'text-green-500',
    idle: 'text-white/50',
    degraded: 'text-amber-400',
    'not-landing': 'text-red-500',
    blocked: 'text-red-500',
    offline: 'text-red-500',
    dead: 'text-red-500',
}

export const DIAGNOSES: Diagnosis[] = ['healthy', 'idle', 'degraded', 'not-landing', 'blocked', 'offline', 'dead']

// Prose for the legend. Each entry says what the state *is* and what mechanism
// produces it, because "degraded" on its own tells an operator nothing about
// whether to act. `actionable` drives the "needs a human" grouping.
export const STATUS_COPY: Record<
    Diagnosis,
    { title: string; summary: string; detail: string; actionable: boolean }
> = {
    healthy: {
        title: 'Healthy',
        summary: 'Players are on it and the numbers add up.',
        detail:
            'The server is stating its own roster, so the player count is exact rather than ' +
            'inferred. A server at capacity is healthy too - genuinely full is not a fault.',
        actionable: false,
    },
    idle: {
        title: 'Idle',
        summary: 'Empty, not broken.',
        detail:
            'Nobody has played here in the last half hour, or the server was deployed a few ' +
            'minutes ago and has not reported yet.',
        actionable: false,
    },
    degraded: {
        title: 'Degraded',
        summary: 'Its player count is a guess, because it is not stating who is on it.',
        detail:
            'A current server reports exactly who is on it every ~30 seconds, and each report ' +
            'replaces the last, so the count is right and fixes itself. This one is not doing ' +
            'that, so its count is assembled from join reservations and drifts upward. Nothing ' +
            'is being refused because of it - a player is only ever turned away on a roster we ' +
            'know is complete. The fix is a game build carrying the population reporter, not ' +
            'anything on this screen.',
        actionable: false,
    },
    'not-landing': {
        title: 'Not landing',
        summary: 'Taking joins and landing nobody.',
        detail:
            'The server answers the backend, so the machine is up, but no join has turned into ' +
            'a spawn recently. Players see it in the lobby, pick it, and never get in; the lobby ' +
            'has already hidden it. The game process is stuck rather than the box, so restart ' +
            'the container first, and rebuild only if that does not bring it back.',
        actionable: true,
    },
    blocked: {
        title: 'Blocked',
        summary: 'Players are stranded mid-match on a server that stopped talking to us.',
        detail:
            'Their matches stay open until the orphan sweep closes them, a few hours after the ' +
            'spawn, and a swept match pays no earnings. Until then they sit unfinished in the ' +
            "player's history. This is the one to act on. It used to mean \"full of slots " +
            'nobody is playing in\" - that state can no longer refuse anybody, so it is not an ' +
            'emergency any more and no longer appears here.',
        actionable: true,
    },
    dead: {
        title: 'Died',
        summary: 'It was reporting every ~30 seconds and has been silent for 5 minutes.',
        detail:
            'A current server reports its roster every ~30 seconds even with nobody on it, so ' +
            'silence from one that had been reporting usually means the game server process is ' +
            'not running: the VM is stopped, terminated or unreachable on GCP. A server that was ' +
            'drained or stopped on purpose goes silent the same way, so check that first. It is ' +
            'no longer offered to players and will not recover on its own; Rebuild it from its ' +
            'card. Distinct from Offline, which is the cautious verdict for a server we simply ' +
            'have not heard from and often needs no action at all.',
        actionable: true,
    },
    offline: {
        title: 'Offline',
        // The wording that caused real confusion: it used to say "the machine is
        // probably gone", which was wrong for the common case -- a good server
        // nobody had joined for six hours looked exactly like a dead one,
        // because reporting only happened when players did something.
        //
        // A server on a current build heartbeats every ~30s whether or not
        // anybody is playing, so for those this really does mean gone. The old
        // caveat stays because both kinds of build are in the fleet during the
        // rollout, and reading "dead" next to a healthy idle server is how staff
        // learned to distrust this column in the first place.
        summary: 'No contact for hours and nobody on it.',
        detail:
            'On a current build a server reports every ~30 seconds even with nobody on it, so ' +
            'silence really does mean the machine is gone. On an older build it only reported ' +
            'when players did something, so a perfectly good server nobody has joined for about ' +
            'six hours goes quiet and lands here on its own - it will usually still accept ' +
            'players and flips back the moment somebody joins. If the server also shows as not ' +
            'reporting, assume the second case and try joining before writing it off. On dev, ' +
            'genuinely dead is common: those machines get reclaimed and need redeploying rather ' +
            'than restarting.',
        actionable: true,
    },
}
